import type { LoginResponse } from "@octadiam/contracts";
import type { AppEnvironment } from "./store";
import { apiUrl } from "./deployment-config";

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export async function apiBlob(path: string, token?: string | null, environment?: AppEnvironment): Promise<Blob> {
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(`${apiUrl(environment || persistedEnvironment())}${path}`, { headers });
  if (!response.ok) {
    throw new ApiError(`File request failed (${response.status})`, response.status);
  }
  return response.blob();
}

function persistedEnvironment(): AppEnvironment {
  try {
    const saved = JSON.parse(localStorage.getItem("octadiam-web-session") || "{}") as {
      state?: { environment?: AppEnvironment };
    };
    return saved.state?.environment === "production" ? "production" : "test";
  } catch {
    return "test";
  }
}

export async function api<T>(
  path: string,
  options: RequestInit = {},
  token?: string | null,
  environment?: AppEnvironment
): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(`${apiUrl(environment || persistedEnvironment())}${path}`, { ...options, headers });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const data = await response.json() as { message?: string | string[]; error?: string };
      message = Array.isArray(data.message) ? data.message.join("; ") : data.message || data.error || message;
    } catch {
      // Preserve the HTTP fallback message.
    }
    throw new ApiError(message, response.status);
  }
  return response.status === 204 ? undefined as T : response.json() as Promise<T>;
}

export function login(username: string, password: string, environment: AppEnvironment) {
  return api<LoginResponse>("/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password })
  }, null, environment);
}

export type EntityEndpoint = {
  title: string;
  description: string;
  path: string;
  form?: string;
};

export const entityEndpoints: Record<string, EntityEndpoint> = {
  designs: {
    title: "Jewelry designs",
    description: "Design records, costing, media and version history.",
    path: "/v1/jewelry/designs",
    form: "designs"
  },
  jobCards: {
    title: "Production job cards",
    description: "Workshop stages, repairs, material reconciliation and documents.",
    path: "/v1/operations/job-cards",
    form: "job_cards"
  },
  inventory: {
    title: "Inventory",
    description: "Stones, precious metals, finished jewelry and movements.",
    path: "/v1/operations/inventory",
    form: "inventory"
  },
  diamondStock: {
    title: "Diamond stock",
    description: "Search the synchronized supplier diamond catalog.",
    path: "/v1/diamond-stock",
    form: "diamond_stock"
  },
  customers: {
    title: "Customers",
    description: "Customer profiles, costing links, quotations and attachments.",
    path: "/v1/reference/customers?limit=200",
    form: "customers"
  },
  employees: { title:"Employees", description:"Secure employee master, employment details and Directus synchronization.", path:"/v1/employees", form:"employees" },
  suppliers: {
    title: "Suppliers",
    description: "Supplier master data and supporting documents.",
    path: "/v1/reference/suppliers?limit=200",
    form: "suppliers"
  },
  receivedInvoices: {
    title: "Invoices received",
    description: "Supplier invoices, attachments and immutable version history.",
    path: "/v1/commercial/received-invoices",
    form: "received_invoices"
  },
  pettyCash: {
    title: "Petty cash",
    description: "Receipts, expenses, invoice evidence and balances.",
    path: "/v1/commercial/petty-cash",
    form: "petty_cash"
  },
  shopify: {
    title: "eShop Management",
    description: "Prepared products, Shopify catalog records, local edits, metafields and controlled synchronization.",
    path: "/v1/shopify/products?limit=200",
    form: "shopify_catalog"
  },
  metalPrices: {
    title: "Gold & silver rates",
    description: "Saved global and Dubai precious-metal rates with controlled on-demand refresh.",
    path: "/v1/jewelry/metal-prices?limit=365",
    form: "gold_prices"
  }
};
